import { useState } from 'react';
import type { FormEvent } from 'react';
import type { OrgInvitationRole } from '@nadoutong/contracts';
import { localizeApiError } from '../api/errorMessages.ts';
import { useCreateOrgInvitation } from '../api/useOrgs.ts';
import { useOrgRoles } from '../api/useRoles.ts';
import { Modal } from './Modal.tsx';
import { useToast } from './Toast.tsx';
import { clipboardErrorKey, useClipboard } from '../hooks/useClipboard.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, cx } from '../ui/index.ts';

/**
 * The invitation composer and the invite-link receipt that follows it.
 *
 * Lifted out of `pages/settings/MembersCard.tsx` (where it was private)
 * so `/organization` can open the same dialog: the governance page owns
 * the invitation list, and listing invitations with no way to create one
 * made that tab a dead end — the entry point lived on another page with
 * nothing on screen saying so.
 *
 * Both callers drive the SAME `useCreateOrgInvitation` mutation, whose
 * success invalidates `ORG_INVITATIONS_KEY(orgId)`. That key is what
 * `useOrgInvitations` reads on both pages, so an invitation sent from
 * either one lands in both lists without a manual refresh.
 *
 * The copy keys stay under `settings.invite_*`: this is the same dialog
 * with the same words, and renaming twenty catalog entries for a prefix
 * would churn the most merge-contended file in the app for no reader-
 * visible gain.
 */

function isOrgInvitationRole(value: string): value is OrgInvitationRole {
  return value === 'admin' || value === 'member' || value === 'finance';
}

interface InviteMemberModalProps {
  readonly orgId: string;
  readonly onClose: () => void;
  readonly onCreated: (inviteUrl: string) => void;
}

export function InviteMemberModal({
  orgId,
  onClose,
  onCreated,
}: InviteMemberModalProps): JSX.Element {
  const t = useT();
  const createM = useCreateOrgInvitation();
  const rolesQ = useOrgRoles(orgId);
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [error, setError] = useState<string | null>(null);
  const customRoles = (rolesQ.data?.roles ?? []).filter((item) => !item.builtin);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setError(t('settings.invite_modal.email_required'));
      return;
    }
    const body = isOrgInvitationRole(role)
      ? { email: trimmed, role }
      : { email: trimmed, roleId: role };
    try {
      const res = await createM.mutateAsync({ orgId, body });
      toast.success(t('settings.invite_modal.toast.sent'));
      onCreated(res.inviteUrl);
    } catch (err) {
      const message = localizeApiError(err, t);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!createM.isPending) onClose();
      }}
      titleId="invite-member-modal-title"
      testId="members-invite-modal"
    >
      <form className="flex flex-col gap-5 p-6 sm:p-8" onSubmit={handleSubmit} noValidate>
        <div className="flex flex-col gap-1">
          <h2 id="invite-member-modal-title" className="m-0 font-display text-title text-ink">
            {t('settings.invite_modal.title')}
          </h2>
          <p className="m-0 text-caption text-ink-low">{t('settings.invite_modal.description')}</p>
        </div>
        <FormField label={t('settings.invite_modal.email')} fieldId="members-invite-email">
          <Input
            id="members-invite-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={createM.isPending}
            required
            maxLength={254}
            data-testid="members-invite-email"
          />
        </FormField>
        <FormField label={t('settings.invite_modal.role')} fieldId="members-invite-role">
          <select
            id="members-invite-role"
            value={role}
            onChange={(e) => setRole(e.currentTarget.value)}
            disabled={createM.isPending}
            data-testid="members-invite-role"
            className="h-9 w-full rounded border border-line bg-raised px-3 text-body text-ink transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60"
          >
            <option value="member">{t('settings.invite_modal.role.member')}</option>
            <option value="admin">{t('settings.invite_modal.role.admin')}</option>
            <option value="finance">{t('settings.invite_modal.role.finance')}</option>
            {customRoles.map((customRole) => (
              <option key={customRole.id} value={customRole.id}>
                {customRole.name}
              </option>
            ))}
          </select>
        </FormField>
        {error !== null ? (
          <p className="m-0 text-label text-bad" role="alert" data-testid="members-invite-error">
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={createM.isPending}
            data-testid="members-invite-cancel"
          >
            {t('settings.invite_modal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createM.isPending}
            data-testid="members-invite-submit"
          >
            {createM.isPending
              ? t('settings.invite_modal.submitting')
              : t('settings.invite_modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface InviteLinkModalProps {
  readonly url: string;
  readonly onClose: () => void;
}

export function InviteLinkModal({ url, onClose }: InviteLinkModalProps): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="invite-link-modal-title"
      testId="members-invite-link-modal"
    >
      <div className="flex flex-col gap-5 p-6 sm:p-8">
        <div className="flex flex-col gap-1">
          <h2 id="invite-link-modal-title" className="m-0 font-display text-title text-ink">
            {t('settings.invite_link.title')}
          </h2>
          <p className="m-0 text-caption text-ink-low">{t('settings.invite_link.description')}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            void copy(url);
          }}
          data-testid="members-invite-link-copy"
          aria-label={t('settings.invite_link.aria_label')}
          className="group flex flex-col items-start gap-2 rounded-lg border border-line bg-raised p-4 text-left transition-colors duration-150 hover:bg-active/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        >
          <span className="font-mono text-mono-label text-ink-low">
            {t('settings.invite_link.click_to_copy')}
          </span>
          <span
            data-testid="members-invite-link-value"
            className="w-full select-all break-all font-mono text-body text-forest"
          >
            {url}
          </span>
          <span
            data-testid="members-invite-link-copy-status"
            className={cx('font-mono text-mono-label', copied ? 'text-ok' : 'text-ink-low')}
          >
            {copied ? t('settings.invite_link.copied') : t('settings.invite_link.copy_anywhere')}
          </span>
        </button>
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="members-invite-link-copy-error"
          >
            {t(clipboardErrorKey(error))}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <Button variant="primary" onClick={onClose} data-testid="members-invite-link-done">
            {t('settings.invite_link.done')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
